/**
 * Getting a request from this page to a source that will not talk to a page.
 *
 * Every adapter in this build was written to `DATA-SOURCES.md` §3's ground
 * rule — the request goes straight from the pilot's browser to the source, with
 * no server of ours in the middle — and every one of them failed in the browser
 * with the same "could not reach it" while passing its whole unit suite. The
 * suite hands each adapter a recorded `fetch`, and the Playwright spec stubs the
 * URL with `page.route`; neither exercises the one thing that actually breaks.
 *
 * **None of these three sources sends `Access-Control-Allow-Origin`.** FR24's API
 * is a server-to-server API, and the readsb globes are static file
 * hosts with no CORS header on them. A browser will not hand a page a response
 * it was not given permission to read, and it reports that refusal as a bare
 * `TypeError` with no status — identical to being offline, which is why
 * `http.ts` has always had to word that error for both cases. So the ground rule
 * as written is not "no server of ours in the middle": it is "no server of ours
 * *required*, and nothing proxied without the pilot choosing it".
 *
 * What this module adds is one URL rewrite in front of `fetch`:
 *
 *  - **In `npm run dev`** the request goes to this app's own dev server under
 *    `/__source/<host>/…`, which `vite.config.ts` proxies to that host. Same
 *    origin, so there is no cross-origin read to refuse, and a pilot running the
 *    app locally needs no configuration at all.
 *  - **On a hosted build** there is no dev server, so the pilot names a relay
 *    (Settings → Flight data sources, or `VITE_SOURCE_RELAY` at build time) and
 *    requests go through that. Nothing is relayed unless they say so, and what
 *    they name is theirs, not ours.
 *  - **Otherwise** the request goes direct, exactly as before.
 *
 * `SOURCE_HOSTS` is a literal list rather than something derived from the
 * adapters' own base URLs because `vite.config.ts` imports this module to build
 * its proxy table, and it cannot follow the `@/` alias into the app graph.
 * `relay.test.ts` asserts the list still covers every host the adapters name, so
 * a new source cannot quietly go unproxied.
 */

/** Every host an adapter in this build sends a request to. */
export const SOURCE_HOSTS = [
  'globe.adsb.lol',
  'globe.airplanes.live',
  'fr24api.flightradar24.com',
] as const;

/**
 * Where the dev server answers for those hosts.
 *
 * Under the app's own origin and prefixed so it cannot collide with a route:
 * `/__source/globe.adsb.lol/globe_history/…`.
 */
export const DEV_RELAY_BASE = '/__source';

/** The token a relay that takes the target as a query parameter puts in its URL. */
export const RELAY_URL_TOKEN = '{url}';

export type RelayConfig =
  /** Straight to the source. What every adapter did before this module existed. */
  | { kind: 'direct' }
  /** Through `base`: a same-origin path prefix, or a relay's own URL. */
  | { kind: 'relay'; base: string };

export const DIRECT: RelayConfig = { kind: 'direct' };

export interface RelayEnvironment {
  /** `import.meta.env.DEV` — the dev server is running and its proxy is live. */
  dev?: boolean;
  /** `VITE_SOURCE_RELAY`, baked in at build time for a hosted deployment. */
  buildTime?: string | undefined;
  /** What the pilot typed in Settings. Wins over the other two. */
  setting?: string | undefined;
}

/**
 * Which of the three ways of reaching a source applies right now.
 *
 * The pilot's own setting first — they are looking at the failure and it is the
 * only one of the three they can change without a rebuild — then the value the
 * build was given, then the dev server's proxy.
 */
export function resolveRelay(environment: RelayEnvironment = {}): RelayConfig {
  const setting = environment.setting?.trim() ?? '';
  if (setting !== '') {
    return { kind: 'relay', base: setting };
  }
  const buildTime = environment.buildTime?.trim() ?? '';
  if (buildTime !== '') {
    return { kind: 'relay', base: buildTime };
  }
  return environment.dev === true ? { kind: 'relay', base: DEV_RELAY_BASE } : DIRECT;
}

export function isSourceHost(hostname: string): boolean {
  return (SOURCE_HOSTS as readonly string[]).includes(hostname.toLowerCase());
}

/**
 * The URL to actually request for `url`.
 *
 * Anything that is not an absolute URL to one of `SOURCE_HOSTS` is returned
 * untouched: a relay the pilot named for their flight data has no business
 * seeing the basemap tiles, and a relative URL is this app's own.
 *
 * Three shapes of relay, because the ones people actually run differ:
 *
 *  - `…?target={url}` — the whole URL, encoded, substituted for the token.
 *    Cloudflare Worker and Netlify-function relays are usually written this way.
 *  - `https://relay.example/` — the target URL appended to the path, which is
 *    the `cors-anywhere` convention most copy-paste relays follow.
 *  - `/__source` — a same-origin path prefix, which is the dev server's proxy
 *    and anything else configured to match it: host first, then the path.
 */
export function relayUrl(url: string, config: RelayConfig): string {
  if (config.kind === 'direct') {
    return url;
  }

  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return url;
  }
  if (!isSourceHost(target.hostname)) {
    return url;
  }

  const base = config.base.trim();
  if (base.includes(RELAY_URL_TOKEN)) {
    return base.replace(RELAY_URL_TOKEN, encodeURIComponent(url));
  }

  const trimmed = base.replace(/\/+$/, '');
  return /^https?:\/\//i.test(trimmed)
    ? `${trimmed}/${url}`
    : `${trimmed}/${target.host}${target.pathname}${target.search}`;
}

/**
 * `fetch`, with every request to a known source host sent the way `config` says.
 *
 * A wrapper rather than an argument to `fetchJson` so that no adapter has to
 * know this exists: `registry.ts` builds one of these and hands it in as the
 * `fetchImpl` the adapters already took for testing.
 */
export function createRelayFetch(
  config: RelayConfig,
  fetchImpl?: typeof globalThis.fetch,
): typeof globalThis.fetch {
  // Resolved per call, not bound once at module load: a test that stubs
  // `globalThis.fetch` after this returns must still be the one that is called.
  const base: typeof globalThis.fetch =
    fetchImpl ?? ((input, init) => globalThis.fetch(input, init));

  if (config.kind === 'direct') {
    return base;
  }

  return (input, init) => {
    if (typeof input === 'string') {
      return base(relayUrl(input, config), init);
    }
    if (input instanceof URL) {
      return base(relayUrl(input.href, config), init);
    }
    // A `Request` carries its own headers and body; nothing in this app builds
    // one, and rewriting it would mean rebuilding it.
    return base(input, init);
  };
}

/**
 * What a pilot has typed is not going to work, in one sentence — or `undefined`.
 *
 * Only the mistakes that are certain: an empty value means "go direct" and is
 * not a mistake, and a relay that is reachable but wrong is something only a
 * request can discover.
 */
export function relayProblem(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
    return undefined;
  }
  return 'A relay has to start with https:// (or http://), or with / for a path on this same site.';
}

/**
 * The dev server's proxy table: which path stands for which host.
 *
 * `vite.config.ts` turns this into its `server.proxy` config. Kept here so the
 * paths this module builds and the paths the server answers on cannot drift.
 */
export function relayProxyTargets(): Record<string, string> {
  return Object.fromEntries(
    SOURCE_HOSTS.map((host) => [`${DEV_RELAY_BASE}/${host}`, `https://${host}`]),
  );
}
